import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Empty, Spin } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getInspirationGalleryData, clearGalleryList } from "../store/inspirationGallery/inspiration-gallery-slice";
import InspirationGalleryHeader from "../Inspiration-gallery-header-right";
import InspirationCard from "../Inspiration-card";
import InspirationSkeletonCard from "../inspiration-skeleton-card";
import "../css/inspiration-gallery-layout.css";

const PAGE_LIMIT = 24;

const Q_TYPE = "type";
const Q_CATEGORY = "category";
const Q_SEARCH = "q";

const parsePlatformFromQuery = (value) => {
    const v = String(value ?? "").trim().toLowerCase();
    if (v === "mobile") return "Mobile";
    return "Web";
};

const filtersFromSearchParams = (params) => ({
    platform: parsePlatformFromQuery(params.get(Q_TYPE)),
    category: params.get(Q_CATEGORY) || null,
    search: (params.get(Q_SEARCH) || "").trim(),
});

const buildSearchParams = ({ platform, category, search }) => {
    const p = new URLSearchParams();
    if (platform === "Mobile") p.set(Q_TYPE, "mobile");
    if (category) p.set(Q_CATEGORY, category);
    if (search) p.set(Q_SEARCH, search);
    return p;
};

const InspirationGalleryListView = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [page, setPage] = useState(0);

    const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);

    const setFilters = useCallback(
        (update) => {
            setSearchParams(
                (prev) => {
                    const cur = filtersFromSearchParams(prev);
                    const next = typeof update === "function" ? update(cur) : { ...cur, ...update };
                    return buildSearchParams(next);
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const { isLoading, inspirationGalleryList, isListNext } = useSelector(
        (state) => state.inspirationGallerySlice
    );


    // 🔥 Initial load & reset on filter/search change
    useEffect(() => {
        dispatch(clearGalleryList());
        setPage(0);
        dispatch(getInspirationGalleryData({ skip: 0, limit: PAGE_LIMIT, filter: { platform: filters.platform, ...(filters.category && { category: filters.category }) }, search: filters.search ? { projectTitle: filters.search } : {} }));
    }, [dispatch, filters.platform, filters.category, filters.search]);

    const loadMore = () => {
        if (!isListNext || isLoading) return;
        const nextPage = page + PAGE_LIMIT;
        setPage(nextPage);
        dispatch(getInspirationGalleryData({ skip: nextPage, limit: PAGE_LIMIT, filter: { platform: filters.platform, ...(filters.category && { category: filters.category }) }, search: filters.search ? { projectTitle: filters.search } : {} }));
    };

    const handleClickView = (item) => navigate(`/dashboard/inspiration-gallery/${item._id}`);

    return (
        <div className="inspiration-gallery-page container">
            <header className="inspiration-list-head-card">
                <div className="inspiration-list-head-main">
                    <div className="inspiration-list-head-titles">
                        <h1 className="inspiration-list-page-title">Inspiration Gallery</h1>
                    </div>
                    <div className="inspiration-list-head-toolbar-wrap">
                        <InspirationGalleryHeader filters={filters} setFilters={setFilters} />
                    </div>
                </div>
            </header>

            <InfiniteScroll
                dataLength={inspirationGalleryList.length}
                next={loadMore}
                hasMore={isListNext}
                loader={
                    <div className="inspiration-gallery-bottom-loader" role="status" aria-live="polite">
                        <Spin size="small" />
                        <span className="inspiration-gallery-bottom-loader__text">Loading more…</span>
                    </div>
                }
            >
                <Row gutter={[20, 20]} className="inspiration-gallery-grid-wrap">
                    {isLoading && page === 0 ? (
                        Array.from({ length: 8 }).map((_, index) => (
                            <Col key={index} xs={24} sm={12} md={8} lg={6}>
                                <InspirationSkeletonCard />
                            </Col>
                        ))
                    ) : inspirationGalleryList.length === 0 ? (
                        <Col span={24}>
                            <div className="inspiration-gallery-empty">
                                <Empty
                                    description="No inspiration matches your filters yet"
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                            </div>
                        </Col>
                    ) : (
                        inspirationGalleryList.map((item) => (
                            <Col key={item._id} xs={24} sm={12} md={8} lg={6}>
                                <InspirationCard item={item} onOpen={handleClickView} />
                            </Col>
                        ))
                    )}
                </Row>
            </InfiniteScroll>
        </div>
    );
};

export default InspirationGalleryListView;
